import React from 'react';
import { connect } from 'react-redux';
import { translate } from 'react-i18next';
import { Route } from 'react-router';
import CBMediaQuery from '../shared/CBMediaQuery';
import { getStyle, TYPE_CHAIN_CARE_SERVICE } from '../../utils/utils';
import ChainNavbar from './ChainNavbar';
import VehicleListView from '../OrganizationGarageView/OrganizationGarageComponents/VehicleListView';
import TasksLayoutView from '../TasksView/TasksLayoutView';
import MessagesLayoutView from '../MessagesView/MessagesLayoutView';
import InviteToChainCareServiceModal from './components/InviteToChainCareServiceModal';
import AddChainCareServiceVehicleModal from './components/AddChainCareServiceVehicleModal';
import NoChainView from './NoChainView';
import ChainUpcomingMaintenancesView from './ChainUpcomingMaintenancesView';
import ChainPartsUsageView from './ChainPartsUsageView';
import ChainInvoicingView from './ChainInvoicingView';
import {
  fetchChainCareServiceVehicles as _fetchChainCareServiceVehicles,
  fetchChainCareServiceInvitations as _fetchChainCareServiceInvitations,
  fetchChainMemberships as _fetchChainMemberships,
  assignWorkshopToVehicle as _assignWorkshopToVehicle,
} from '../../actions/chainActions';
import { setLastListUrl as _setLastListUrl } from '../../actions/appActions';

class ChainHomeView extends React.Component {
  state = {
    invitationModalIsOpen: false,
    addVehicleModalIsOpen: false,
  };

  // The chain care service vehicle list lives in a single Redux slot shared by
  // the vehicles tab (VehicleListView, which fetches it with the persisted
  // organization filter) and the task/message vehicle pickers (which need the
  // full, unfiltered list). To keep the parent's unfiltered fetch from
  // clobbering VehicleListView's filtered fetch, we only fetch here when the
  // vehicles tab is NOT the active route.
  isVehicleListRoute = (pathname) => /\/chain\/[^/]+\/vehicles\/?$/.test(pathname || '');

  componentDidMount = () => {
    const {
      selectedChainId,
      match,
      fetchChainCareServiceVehicles,
      fetchChainCareServiceInvitations,
      fetchChainMemberships,
      setLastListUrl,
      location,
    } = this.props;

    const chainId = selectedChainId || match.params.chainId;

    if (chainId) {
      if (!this.isVehicleListRoute(location.pathname)) {
        fetchChainCareServiceVehicles(chainId, { ordering: 'registration_number' });
      }
      fetchChainCareServiceInvitations(chainId);
      fetchChainMemberships(chainId);
    }

    setLastListUrl(location.pathname);
  };

  componentDidUpdate(prevProps) {
    const {
      selectedChainId,
      match,
      fetchChainCareServiceVehicles,
      fetchChainCareServiceInvitations,
      fetchChainMemberships,
      setLastListUrl,
      location,
    } = this.props;

    if (selectedChainId !== prevProps.selectedChainId && selectedChainId) {
      // On the vehicles tab VehicleListView reacts to the chain change and
      // refetches with the persisted filter, so skip the unfiltered fetch here.
      if (!this.isVehicleListRoute(location.pathname)) {
        fetchChainCareServiceVehicles(selectedChainId, { ordering: 'registration_number' });
      }
      fetchChainCareServiceInvitations(selectedChainId);
      fetchChainMemberships(selectedChainId);
    }

    if (location.pathname !== prevProps.location.pathname) {
      // When leaving the vehicles tab, restore the full (unfiltered) list for
      // the task/message vehicle pickers that share this Redux slot.
      const chainId = selectedChainId || match.params.chainId;
      if (chainId
        && this.isVehicleListRoute(prevProps.location.pathname)
        && !this.isVehicleListRoute(location.pathname)) {
        fetchChainCareServiceVehicles(chainId, { ordering: 'registration_number' });
      }
      setLastListUrl(location.pathname);
    }
  }

  fetchVehicles = (params) => {
    const { selectedChainId, fetchChainCareServiceVehicles } = this.props;
    if (selectedChainId) {
      fetchChainCareServiceVehicles(selectedChainId, params);
    }
  };

  openInvitationModal = () => {
    this.setState({ invitationModalIsOpen: true });
  };

  closeInvitationModal = () => {
    this.setState({ invitationModalIsOpen: false });
  };

  openAddVehicleModal = () => {
    this.setState({ addVehicleModalIsOpen: true });
  };

  closeAddVehicleModal = () => {
    this.setState({ addVehicleModalIsOpen: false });
  };

  render() {
    const {
      history,
      t,
      match,
      selectedChainId,
      user,
      chainCareServiceVehicles,
      chainCareServiceInvitations,
    } = this.props;

    const { invitationModalIsOpen, addVehicleModalIsOpen } = this.state;

    const chainId = selectedChainId || match.params.chainId;

    if (!chainId) {
      return <NoChainView />;
    }

    const chain = user && user.chains && user.chains.find(function (c) { return c.id == chainId; });
    const hideInvoicing = chain && chain.hide_invoicing;
    const userRole = chain && chain.user_role ? chain.user_role : null;

    // Vehicles are returned as full Vehicle objects from the API
    const vehiclesForList = chainCareServiceVehicles;

    return (
      <CBMediaQuery>
        {screenSize => (
          <div>
            <ChainNavbar chainId={chainId} chain={chain} userRole={userRole} />
            <div style={getStyle(screenSize, styles, 'contentContainer')}>
              <Route
                exact
                path='/chain/:chainId/tasks/'
                render={() => (
                  <TasksLayoutView
                    vehicles={vehiclesForList}
                    chainId={chainId}
                    hasFeatureTasksAndMessages
                  />
                )}
              />
              <Route
                exact
                path='/chain/:chainId/upcoming-maintenances/'
                render={() => (
                  <ChainUpcomingMaintenancesView chainId={chainId} />
                )}
              />
              <Route
                exact
                path='/chain/:chainId/vehicles/'
                render={() => (
                  <VehicleListView
                    inviteToCareService={this.openInvitationModal}
                    onAddCar={this.openAddVehicleModal}
                    vehicles={vehiclesForList}
                    fetchVehicles={this.fetchVehicles}
                    goBack={history.goBack}
                    chainId={chainId}
                    chain={chain}
                    listType={TYPE_CHAIN_CARE_SERVICE}
                    hasFeatureTasksAndMessages
                    chainMemberships={this.props.chainMemberships}
                    assignWorkshopToVehicle={this.props.assignWorkshopToVehicle}
                  />
                )}
              />
              <Route
                path='/chain/:chainId/messages/'
                render={() => (
                  <MessagesLayoutView
                    vehicles={vehiclesForList}
                    chainId={chainId}
                    hasFeatureTasksAndMessages
                  />
                )}
              />
              <Route
                path='/chain/:chainId/invoicing/'
                render={() => (
                  <ChainInvoicingView chainId={chainId} />
                )}
              />
              <Route
                exact
                path={[
                  '/chain/:chainId/parts-usage/',
                  '/chain/:chainId',
                  '/',
                ]}
                render={() => (
                  <ChainPartsUsageView chainId={chainId} chain={chain} />
                )}
              />
            </div>

            {invitationModalIsOpen && (
              <InviteToChainCareServiceModal
                modalIsOpen={invitationModalIsOpen}
                selectedChainId={chainId}
                closeModal={this.closeInvitationModal}
              />
            )}

            {addVehicleModalIsOpen && (
              <AddChainCareServiceVehicleModal
                modalIsOpen={addVehicleModalIsOpen}
                chainId={chainId}
                closeModal={this.closeAddVehicleModal}
                onVehicleAdded={() => this.fetchVehicles({ ordering: 'registration_number' })}
              />
            )}

          </div>
        )}
      </CBMediaQuery>
    );
  }
}

const styles = {
  default: {
    contentContainer: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center',
    },
  },
  medium: {
    contentContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
};

function mapStateToProps(state) {
  return {
    user: state.authUser.user,
    selectedChainId: state.chain.selectedChainId,
    chainCareServiceVehicles: state.chain.chainCareServiceVehicles,
    chainCareServiceInvitations: state.chain.chainCareServiceInvitations,
    chainMemberships: state.chain.chainMemberships,
  };
}

export default connect(
  mapStateToProps,
  {
    fetchChainCareServiceVehicles: _fetchChainCareServiceVehicles,
    fetchChainCareServiceInvitations: _fetchChainCareServiceInvitations,
    fetchChainMemberships: _fetchChainMemberships,
    assignWorkshopToVehicle: _assignWorkshopToVehicle,
    setLastListUrl: _setLastListUrl,
  },
)(translate('ChainHomeView')(ChainHomeView));
